<template>
  <v-menu :offset="14">
    <template #activator="{ props, isActive }">
      <button
        v-bind="props"
        type="button"
        class="jp-role-button"
        aria-label="Switch JP role"
        :class="{ active: isActive }"
      >
        <span class="jp-role-label text-body-1">
          JobNav {{ currentRoleLabel ?? 'Role' }}
        </span>
        <v-icon
          icon="$chevron-down"
          :size="20"
        />
      </button>
    </template>
    <section class="dropdown-container">
      <ul class="role-list">
        <li
          v-for="role in JP_ROLES"
          :key="role.value"
          class="role-item"
          :class="{ 'role-item--active': role.value === currentJpRole }"
        >
          <button
            type="button"
            class="role-item-btn text-body-2"
            :disabled="role.value === currentJpRole"
            @click="onSelect(role.value)"
          >
            {{ role.title }}
          </button>
        </li>
      </ul>
    </section>
  </v-menu>
</template>

<script setup>
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useAppAuthData } from '@/stores/app/authData';
import { switchJpRole } from '@/api/settings';

const JP_ROLES = [
  { title: 'Viewer', value: 'viewer' },
  { title: 'Editor', value: 'editor' },
  { title: 'Operator', value: 'operator' },
  { title: 'Admin', value: 'admin' },
];

defineOptions({ name: 'JpRoleDropdown' });

const JP_ROLE_NAMES = ['jobNavViewer', 'jobNavEditor', 'jobNavOperator', 'jobNavAdmin'];

const { authData } = storeToRefs(useAppAuthData());

const currentJpRole = computed(() => {
  const match = authData.value.rbac?.roleNames?.find((r) => JP_ROLE_NAMES.includes(r));
  if (!match) return null;
  return match.replace('jobNav', '').toLowerCase();
});
const currentRoleLabel = computed(
  () => JP_ROLES.find((r) => r.value === currentJpRole.value)?.title ?? null,
);

const onSelect = async (role) => {
  await switchJpRole(role);
  window.location.reload();
};
</script>

<style lang="less" scoped>
.jp-role-button {
  display: inline-flex;
  align-items: center;
  justify-content: space-between;

  .jp-role-initial {
    color: @forest-green;
    width: 32px;
    height: 32px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background-color: @bg-dark;
  }

  .jp-role-label {
    color: @text-white;
    padding: 0px 8px;
  }

  :deep(svg path) {
    fill: @icon-white;
  }

  :deep(.v-icon) {
    transition: 300ms all ease-in-out;
  }

  &.active {
    :deep(.v-icon) {
      transform: rotateZ(180deg);
    }
  }
}

.dropdown-container {
  background-color: @bg-default;
  border-radius: 4px;
  border: 1px solid @stroke-dark;
  padding: 12px 16px;
}

li {
  list-style-type: none;
}

.role-list {
  min-width: 160px;
}

.role-item {
  margin-top: 4px;
  border-radius: 8px;
  display: flex;
  align-items: center;

  &--active {
    background-color: @bg-dark;
    border: 1px solid @stroke-default;
  }

  &:not(.role-item--active):hover {
    background-color: @bg-dark;
  }
}

.role-item-btn {
  display: inline-flex;
  align-items: center;
  width: 100%;
  color: @text-darkest;
  padding: 6px 8px;

  &:disabled {
    cursor: default;
  }
}
</style>
